import React, { useState, useEffect, useRef } from 'react';
import { Button, Table, Col, Row, Modal, ModalHeader, ModalBody, ModalFooter, Label } from 'reactstrap';
import { Chart, registerables } from 'chart.js';
import { H2, H3 } from '../../../../AbstractElements';
import DollarInput from '../../../common/DollarInput';
import DecimalInput from '../../../common/DecimalInput';
import * as echarts from 'echarts';
import { toast } from 'react-toastify';
import { RootState } from '../../../../ReduxToolkit/Store';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { setHomeLoanData } from '../../../../ReduxToolkit/Reducers/HomeLoanSlice';
import { useGetRequestQuery } from '../../../../Hooks/useGetRequestQuery';
import { getAssetsPropertyByClientIdService } from '../../../../Services/PublicPageServices';

const CRMHomeLoan: React.FC = () => {
    const dispatch = useDispatch();
    const { loanAmount, interestRate, loanTerm, extraPayment } = useSelector((state: RootState) => state.homeLoan);
    const [results, setResults] = useState<any>(null);
    const [monthlyBreakdown, setMonthlyBreakdown] = useState<string>('');
    const [standardBalances, setStandardBalances] = useState<number[]>([]);
    const [extraBalances, setExtraBalances] = useState<number[]>([]);
    const [months, setMonths] = useState<number[]>([]);
    const [modal, setModal] = useState(false);
    const chartRef = useRef<HTMLCanvasElement | null | any>(null);
    const [checkboxValue, setCheckboxValue] = useState(false);
    Chart.register(...registerables);
    const [assetsPropertyData, setAssetsPropertyData] = useState<any>(null);
    const userData = useSelector((state: RootState) => state.auth);

    const safeParseFloat = (value: string) => {
        const cleanedValue = value.replace(/,/g, '');
        const parsedValue = parseFloat(cleanedValue);
        return isNaN(parsedValue) ? 0 : parsedValue;
    };

    const {
        data: assetsProperty,
        refetch: refetchAssetsProperty,
        isLoading: assetsPropertyLoading,
        isError: assetsPropertyIsError,
    } = useGetRequestQuery(
        ['getAssetsPropertyByClientId', userData?.nameidentifier],
        () => getAssetsPropertyByClientIdService(userData?.nameidentifier as any),
        {
            enabled: true,
        }
    );
    useEffect(() => {
        if (assetsProperty?.data && !assetsPropertyIsError && !assetsPropertyLoading) {
            const ownerOccupiedProperties = assetsProperty.data.filter(
                (property: any) => property.propertyUse === "Owner Occupied"
            );
            setAssetsPropertyData(ownerOccupiedProperties[0]);
        }
    }, [assetsProperty, assetsPropertyIsError, assetsPropertyLoading]);

    const calculateDifference = (standardTerm: string, extraTerm: string) => {
        const standard = standardTerm.split(" ");
        const extra = extraTerm.split(" ");

        const standardYears = parseInt(standard[0]);
        const standardMonths = parseInt(standard[2]);

        const extraYears = parseInt(extra[0]);
        const extraMonths = parseInt(extra[2]);

        let yearsDiff = standardYears - extraYears;
        let monthsDiff = standardMonths - extraMonths;

        if (monthsDiff < 0) {
            monthsDiff += 12;
            yearsDiff -= 1;
        }

        return `${yearsDiff} years ${monthsDiff} months`;
    };

    const calculateLoan = () => {
        if (loanAmount === "" || interestRate === "") {
            toast.error("Please enter valid loan details.");
            return;
        }

        const loanAmountValue = safeParseFloat(loanAmount);
        const interestRateValue = safeParseFloat(interestRate) / 100 / 12;
        const loanTermValue = parseInt(loanTerm) * 12;
        const extraPaymentValue = safeParseFloat(extraPayment);

        let standardMonthlyPayment = (loanAmountValue * interestRateValue) / (1 - Math.pow(1 + interestRateValue, -loanTermValue));
        let remainingBalance = loanAmountValue;
        let totalInterestStandard = 0;
        let totalInterestExtra = 0;
        let monthsStandard = 0;
        let monthsExtra = 0;
        let monthlyBreakdownHTML = "";
        let currentDate = new Date();
        const standardBalancesTemp: number[] = [];
        const monthsTemp: number[] = [];
        let totalRepaymentsStandard = 0;
        let totalRepaymentsExtra = 0;

        // Standard Loan Calculation
        // Standard Loan Calculation
        remainingBalance = loanAmountValue;
        for (let month = 1; month <= loanTermValue; month++) {
            let interest = remainingBalance * interestRateValue;
            let principal = standardMonthlyPayment - interest;

            // Prevent overpayment in the last month
            if (principal > remainingBalance) {
                principal = remainingBalance;
                standardMonthlyPayment = principal + interest; // Adjust last payment
            }

            remainingBalance -= principal;
            totalInterestStandard += interest;
            monthsStandard = month;
            totalRepaymentsStandard += standardMonthlyPayment;

            // Ensure balance doesn't go negative
            if (remainingBalance < 0) remainingBalance = 0;

            standardBalancesTemp.push(remainingBalance);
            monthsTemp.push(month);

            if (remainingBalance <= 0) break; // Exit early if paid off
        }
        let standardEndDate = new Date();
        standardEndDate.setMonth(standardEndDate.getMonth() + monthsStandard);

        // Extra Payment Calculation
        remainingBalance = loanAmountValue;
        const extraBalancesTemp: number[] = [];
        currentDate = new Date();
        while (remainingBalance > 0) {
            let interest = remainingBalance * interestRateValue;
            let principal = (standardMonthlyPayment + extraPaymentValue) - interest;
            if (principal > remainingBalance + interest) {
                principal = remainingBalance + interest;
            }
            let totalPayment = principal + interest;
            remainingBalance -= principal;
            totalInterestExtra += interest;
            monthsExtra++;
            totalRepaymentsExtra += (standardMonthlyPayment + extraPaymentValue);

            if (remainingBalance < 0) remainingBalance = 0;
            extraBalancesTemp.push(remainingBalance);

            monthlyBreakdownHTML += `
                <tr>
                    <td>${monthsExtra}</td>
                    <td>${currentDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                    <td>$${remainingBalance.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</td>
                    <td>$${standardMonthlyPayment.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</td>
                    <td>$${extraPaymentValue.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</td>
                    <td>$${totalPayment.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</td>
                    <td>$${interest.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</td>
                    <td>$${Math.max(remainingBalance, 0).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</td>
                </tr>
            `;
            currentDate.setMonth(currentDate.getMonth() + 1);
        }

        let extraEndDate = new Date();
        extraEndDate.setMonth(extraEndDate.getMonth() + monthsExtra);

        const comparedTerm = calculateDifference(
            `${Math.floor(monthsStandard / 12)} years ${monthsStandard % 12} months`,
            `${Math.floor(monthsExtra / 12)} years ${monthsExtra % 12} months`
        );

        const formatNumber = (value: number) => {
            return value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        };

        setResults({
            standardPayment: formatNumber(standardMonthlyPayment),
            standardInterest: formatNumber(totalInterestStandard),
            standardTerm: `${Math.floor(monthsStandard / 12)} years ${monthsStandard % 12} months`,
            standardDate: standardEndDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
            comparedTerm: comparedTerm,
            extraPaymentAmount: formatNumber(standardMonthlyPayment + extraPaymentValue),
            extraInterest: formatNumber(totalInterestExtra),
            extraTerm: `${Math.floor(monthsExtra / 12)} years ${monthsExtra % 12} months`,
            extraDate: extraEndDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
            savings: formatNumber(totalInterestStandard - totalInterestExtra),
            repaymentSavings: formatNumber(totalRepaymentsStandard - totalRepaymentsExtra),
            totalRepaymentsStandard: formatNumber(totalRepaymentsStandard),
            totalRepaymentsExtra: formatNumber(totalRepaymentsExtra)
        });

        setMonthlyBreakdown(monthlyBreakdownHTML);
        setStandardBalances(standardBalancesTemp);
        setExtraBalances(extraBalancesTemp);
        setMonths(monthsTemp);
    };

    useEffect(() => {
        if (results && chartRef.current) {
            const chartInstance = echarts.init(chartRef.current);
            const yearLabels = months.map((month, index) => {
                const startYear = new Date().getFullYear();
                const monthOffset = index;
                const year = startYear + Math.floor((new Date().getMonth() + monthOffset) / 12);
                return year.toString();
            });

            const option = {
                title: { text: '' },
                tooltip: { trigger: 'axis', axisPointer: { type: 'cross', label: { backgroundColor: '#6a7985' } } },
                legend: {
                    data: [
                        {
                            name: 'Old Loan',
                            icon: 'rect',
                            itemStyle: {
                                borderRadius: '5px',
                                color: '#ff0000'
                            },
                            textStyle: {
                                color: '#ff0000',
                                fontSize: 18,
                                fontWeight: 'bold'
                            }
                        },
                        {
                            name: 'MMP Loan',
                            icon: 'rect',
                            itemStyle: {
                                borderRadius: '5px',
                                color: 'green'
                            },
                            textStyle: {
                                color: 'green',
                                fontSize: 18,
                                fontWeight: 'bold'
                            }
                        }
                    ],
                    textStyle: {
                        fontSize: 18,
                        fontWeight: 'bold'
                    },
                    itemWidth: 60,
                    itemHeight: 30,
                },
                toolbox: { feature: { saveAsImage: {} } },
                grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
                xAxis: [{ type: 'category', boundaryGap: false, data: yearLabels }],
                yAxis: [{ type: 'value' }],
                series: [
                    { name: 'Old Loan', type: 'line', areaStyle: {}, emphasis: { focus: 'series' }, itemStyle: { color: '#FF0000' }, data: standardBalances },
                    { name: 'MMP Loan', type: 'line', areaStyle: {}, emphasis: { focus: 'series' }, itemStyle: { color: 'green' }, data: extraBalances }
                ]
            };
            chartInstance.setOption(option);
            return () => chartInstance.dispose();
        }
    }, [results, months, standardBalances, extraBalances]);

    const toggleModal = () => setModal(!modal);

    const handleValueChange = (name: string, value: string | Date | null) => {
        dispatch(setHomeLoanData({ ...{ loanAmount, interestRate, loanTerm, extraPayment }, [name]: value }));
    };

    useEffect(() => {
        if (loanAmount !== "" && interestRate !== "") calculateLoan();
    }, []);

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCheckboxValue(event.target.checked);

        if (event.target.checked) {
            dispatch(setHomeLoanData({
                loanAmount: assetsPropertyData?.currentMortgage,
                interestRate: assetsPropertyData?.interestRate,
                extraPayment: assetsPropertyData?.loanDetailsMonthlyRepayment,
                loanTerm: loanTerm
            }));
        }
    };
    return (
        <div className='container-fluid'>
            <div className="table-responsive h-100 py-4">
                <section className='bg-white'>
                    <div className="scrollable-table-container">
                        <table className="scrollable-table" style={{ marginTop: '3%' }}>
                            <div className="d-flex justify-content-between align-items-center">
                                <H3 className="heading-color f-w-900">Home Loan</H3>
                                <Button onClick={toggleModal}>Assumptions & Disclaimer</Button>
                            </div>

                            <Row className='mt-4'>
                                <div className='d-flex mt-2 flex-wrap'>
                                    <Col xs={12} style={{ paddingInline: '2px', width: '10%' }}>
                                        <Label className='f-w-900'>Loan Amount</Label>
                                        <DollarInput
                                            label=""
                                            name="loanAmount"
                                            value={loanAmount}
                                            setValue={handleValueChange}
                                            placeholder=""
                                            design="default"
                                            limit={7}
                                            tabIndex={1}
                                        />
                                    </Col>
                                    <Col xs={12} md={2} style={{ paddingInline: '2px', width: '10%' }}>
                                        <Label className='f-w-900'>Interest Rate</Label>
                                        <DecimalInput
                                            label=""
                                            name="interestRate"
                                            value={interestRate}
                                            setValue={handleValueChange}
                                            placeholder=""
                                            design="default"
                                            tabIndex={2}
                                            limit={4}
                                        />
                                    </Col>
                                    <Col xs={12} md={2} style={{ paddingInline: '2px', width: '10%' }}>
                                        <Label className='f-w-900'>Savings</Label>
                                        <DollarInput
                                            label=""
                                            name="extraPayment"
                                            value={extraPayment}
                                            setValue={handleValueChange}
                                            placeholder=""
                                            design="default"
                                            limit={7}
                                            tabIndex={3}
                                        />
                                    </Col>
                                    <Button color="primary" style={{ marginTop: '10px' }} className='mx-2' onClick={calculateLoan} >Calculate</Button>
                                </div>
                                {results && (
                                    <>
                                        <div style={{ borderBottom: '2px solid #0000ff', marginTop: '7px' }}></div>
                                        <Row className="mt-4">
                                            <Col xs={12}>
                                                <H2 className="f-w-900" style={{ color: 'green' }}>Your Savings with Mortgage Mastery Program Loan Structure</H2>
                                            </Col>
                                        </Row>

                                        <Row className="mt-3 mb-4 g-3">
                                            {/* Old Bank Loan Structure */}
                                            <Col xs={12} md={3}>
                                                <Row className="mt-2 px-2">
                                                    <Col xs={12}>
                                                        <h3 className="fw-bold text-primary">Old Bank Loan</h3>
                                                    </Col>
                                                </Row>
                                                <div className="card h-100">
                                                    <div className="card-body p-0">
                                                        <table className="table borderless-table">
                                                            <tbody>
                                                                <tr>
                                                                    <td className="text-start py-3" style={{ border: "none" }}>Years to Pay Off:</td>
                                                                    <td className="py-3" style={{ border: "none" }}>{results.standardTerm}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="text-start py-3" style={{ border: "none" }}>Total Repayments:</td>
                                                                    <td className="py-3" style={{ border: "none" }}>${results.totalRepaymentsStandard}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="text-start py-3" style={{ border: "none" }}>Total Interest:</td>
                                                                    <td className="py-3" style={{ border: "none" }}>${results.standardInterest}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>

                                                    </div>
                                                </div>
                                            </Col>

                                            {/* Interest Saved */}
                                            <Col xs={12} md={3}>
                                                <Row className="mt-2 px-2">
                                                    <Col xs={12}>
                                                        <h3 className="fw-bold text-primary">Mortgage Mastery Loan</h3>
                                                    </Col>
                                                </Row>
                                                <div className="card h-100">
                                                    <div className="card-body p-0">
                                                        <table className="table borderless-table">
                                                            <tbody>
                                                                <tr>
                                                                    <td className="py-3">Years to Pay Off:</td>
                                                                    <td className="py-3">{results.extraTerm}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="py-3">Total Repayments:</td>
                                                                    <td className="py-3">${results.totalRepaymentsExtra}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="py-3">Total Interest:</td>
                                                                    <td className="py-3">${results.extraInterest}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>

                                                    </div>
                                                </div>
                                            </Col>

                                            {/* Years Saved */}
                                            <Col xs={12} md={3}>
                                                {/* <Row className="mt-2 px-3"> */}
                                                    <div className="green-table text-white">
                                                        <h3 className="fw-bold m-0">Your Savings</h3>
                                                    </div>
                                                {/* </Row> */}
                                                <div className="card h-100">
                                                    <div className="card-body p-0 ">
                                                        <table className="table borderless-table green-table">
                                                            <tbody>
                                                                <tr>
                                                                    <td className="py-3 fw-bold green-table">Years Saved</td>
                                                                    <td className="py-3 fw-bold green-table">{results.comparedTerm}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="py-3 fw-bold green-table">Repayments Saved:</td>
                                                                    <td className="py-3 fw-bold green-table">${results.repaymentSavings}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="py-3 fw-bold green-table">Interest Saved:</td>
                                                                    <td className="py-3 fw-bold green-table">${results.savings}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>


                                        {/* Loan Structures and Chart - Side by Side */}
                                        <div className="mt-4 d-flex">
                                            {/* Chart */}
                                            <Col xs={12} md={9}>
                                                <div ref={chartRef} style={{ height: '490px', width: '1080px' }} />
                                            </Col>

                                        </div>
                                    </>
                                )}
                            </Row>
                            {results && (
                                <>
                                    <div className="d-flex align-items-center mt-4">
                                        <Col xs={12} className='d-flex'>
                                            <H3 className="heading-color f-w-900 text-left">Based on what you have told us you could achieve the following</H3>
                                        </Col>
                                    </div>

                                    <div className="mt-4">
                                        <Row>
                                            <Col md={4} className='pb-3'>
                                                <table className="custom-table">
                                                    <thead style={{ backgroundColor: '#0000ff', color: 'white' }}>
                                                        <tr>
                                                            <th>Home Loan - Original Details</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <span>Loan Amount:</span>
                                                                    <span>${loanAmount}</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <span>Annual Interest Rate:</span>
                                                                    <span>{interestRate}%</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <span>Loan Period in years:</span>
                                                                    <span>30</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    </tbody>

                                                </table>
                                            </Col>
                                            <Col md={4} className='pb-3'>
                                                <table className="custom-table">
                                                    <thead style={{ backgroundColor: '#0000ff', color: 'white' }}>
                                                        <tr>
                                                            <th>Home Loan - Original Loan Summary</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <span>Scheduled Loan Payments:</span>
                                                                    <span>${results.standardPayment}</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <span>Scheduled # of Payments:</span>
                                                                    <span>360</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <span>Start date of loan:</span>
                                                                    <span>{new Date().toLocaleDateString('en-GB', {
                                                                        day: '2-digit',
                                                                        month: 'short',
                                                                        year: 'numeric'
                                                                    })}</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    </tbody>

                                                </table>
                                            </Col>
                                            <Col md={4} className='pb-3'>
                                                <table className="custom-table">
                                                    <thead style={{ backgroundColor: '#0000ff', color: 'white' }}>
                                                        <tr>
                                                            <th>Home Loan - New One</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <span>Current Savings:</span>
                                                                    <span>${extraPayment || 0}</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    </tbody>

                                                </table>
                                            </Col>
                                        </Row>
                                    </div>
                                </>
                            )}
                        </table>
                    </div>
                </section>
                <section className='bg-white'>
                    <div className="scrollable-table-container">
                        <table className="scrollable-table" style={{ marginTop: '3%', fontSize: '13px' }}>
                            {results && (
                                <>
                                    <div className="d-flex align-items-center my-4">
                                        <Col xs={12} className='d-flex'>
                                            <H3 className="heading-color f-w-900 text-left">
                                                New Loan Payment Schedule
                                            </H3>
                                        </Col>
                                    </div>
                                    <div>
                                        <Table bordered hover>
                                            <thead>
                                                <tr>
                                                    <th>Month</th>
                                                    <th>Payment Date</th>
                                                    <th>Open Principal</th>
                                                    <th>Scheduled Payment</th>
                                                    <th>Current Savings</th>
                                                    <th>Total Payment</th>
                                                    <th>Payment Interest</th>
                                                    <th>Closing Principal</th>
                                                </tr>
                                            </thead>
                                            <tbody dangerouslySetInnerHTML={{ __html: monthlyBreakdown }} />
                                        </Table>
                                    </div>
                                </>
                            )}
                        </table>
                    </div>
                </section>
            </div >
            <Modal isOpen={modal} toggle={toggleModal} size='lg'>
                <ModalHeader toggle={toggleModal} style={{ color: '#7030a0' }}>Assumptions & Disclaimer</ModalHeader>
                <ModalBody>
                    <h4 className='f-w-900'>ASSUMPTIONS</h4>
                    <p className='f-w-900'>Time and Interest saved are based upon the following assumptions:</p>
                    <ul className="custom-ul">
                        <li>Calculator only for Owner Occupier Home Loan and not Investment loans</li>
                        <li>Interest is calculated daily and charged monthly</li>
                        <li>Months are assumed to be of equal length</li>
                        <li>One year is assumed to contain exactly 52 weeks or 26 fortnights</li>
                        <li>This implicitly assumes that a year has 364 days rather than the actual 365 or 366.</li>
                        <li>Repayments are on a fortnightly basis over a 30-year loan term</li>
                        <li>Savings are made via Income less Expenses with the difference staying in Home Loan</li>
                        <li>Salary is credited directly to the Home Loan</li>
                        <li>Funds are redrawn to cover expenses</li>
                    </ul>

                    <h4 className='f-w-900 mt-4'>DISCLAIMER</h4>
                    <p>The results from this calculator should be used as an indication only. Results do not represent either quotes or pre-qualifications for the product. Individual institutions apply different formulas. Information such as interest rates quoted, and default figures used in the assumptions are subject to change. This calculator is not intended to be relied on for the purposes of deciding in relation to a financial product. You should consider obtaining advice from a financial services licensee before making any financial decisions.</p>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleModal}>Close</Button>
                </ModalFooter>
            </Modal>
        </div >
    );
};

export default CRMHomeLoan;